import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { scrollToBooking } from "../utils/scrollToSection";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery
} from "@mui/material";
import {
  Public as GlobeIcon,
  ArrowForward as ArrowIcon,
  Event as EventIcon
} from "@mui/icons-material";
import { getContinentBySlug } from "../data/locationData";
import Navbar from "../components/navbar.jsx";
import Footer from "../components/footer";
import BookingForm from "../components/bookingForm";

const ContinentPage = () => {
  const { slug } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const continent = getContinentBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!continent) {
    return (
      <>
        <Navbar />
        <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h4">Location not found</Typography>
          <Button component={Link} to="/" sx={{ mt: 2 }}>Return Home</Button>
        </Container>
        <Footer />
      </>
    );
  }


  return (
    <>
      <Helmet>
        <title>{continent.metaTitle}</title>
        <meta name="description" content={continent.metaDescription} />
        <meta name="keywords" content={`dhol players ${continent.name}, dhol drummers ${continent.name}, Asian wedding entertainment ${continent.name}, Indian wedding drummers ${continent.name}, Punjabi dhol ${continent.name}`} />
        <link rel="canonical" href={`https://imperialdrummers.com/dhol-players/${continent.slug}`} />

        <meta property="og:title" content={continent.metaTitle} />
        <meta property="og:description" content={continent.metaDescription} />
        <meta property="og:url" content={`https://imperialdrummers.com/dhol-players/${continent.slug}`} />
        <meta property="og:type" content="website" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": `Imperial Drummers - ${continent.name}`,
            "description": continent.metaDescription,
            "url": `https://imperialdrummers.com/dhol-players/${continent.slug}`,
            "provider": {
              "@type": "Organization",
              "name": "Imperial Drummers",
              "url": "https://imperialdrummers.com"
            },
            "areaServed": {
              "@type": "Continent",
              "name": continent.name
            },
            "serviceType": "Dhol Entertainment"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://imperialdrummers.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": continent.name,
                "item": `https://imperialdrummers.com/dhol-players/${continent.slug}`
              }
            ]
          })}
        </script>
      </Helmet>

      <Navbar />

      <Box component="main">
        {/* Hero Section */}
        <Box sx={{
          background: 'linear-gradient(135deg, #5C005D 0%, #1a0a2e 100%)',
          color: 'white',
          pt: { xs: 14, md: 18 },
          pb: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <GlobeIcon sx={{ mr: 1, color: '#ffd700' }} />
              <Typography variant="subtitle1" sx={{ color: '#ffd700' }}>
                {continent.name}
              </Typography>
            </Box>

            <Typography
              variant={isMobile ? "h3" : "h1"}
              component="h1"
              sx={{ fontWeight: 'bold', mb: 3 }}
            >
              Professional Dhol Players in {continent.name}
            </Typography>

            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, lineHeight: 1.7, maxWidth: 800 }}>
              {continent.description}
            </Typography>

            <Button
              variant="contained"
              size="large"
              onClick={scrollToBooking}
              startIcon={<EventIcon />}
              sx={{
                px: 4,
                py: 1.5,
                backgroundColor: '#ffd700',
                color: '#1a0a2e',
                fontWeight: 'bold',
                '&:hover': { backgroundColor: '#e6c200' }
              }}
            >
              Get Free Quote
            </Button>
          </Container>
        </Box>

        {/* Countries Grid */}
        <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: '#f8f5fb' }}>
          <Container maxWidth="lg">
            <Typography variant="h2" align="center" sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '1.75rem', md: '3rem' } }}>
              Countries We Serve in {continent.name}
            </Typography>
            <Typography variant="h6" align="center" sx={{ color: '#666', mb: 6, maxWidth: 700, mx: 'auto' }}>
              Select a country to find dhol players for your wedding or event
            </Typography>

            <Grid container spacing={3}>
              {continent.countries.map((country) => (
                <Grid item xs={12} sm={6} md={4} key={country.slug}>
                  <Card
                    component={Link}
                    to={`/dhol-players/${continent.slug}/${country.slug}`}
                    sx={{
                      textDecoration: 'none',
                      display: 'block',
                      height: '100%',
                      '&:hover': { transform: 'translateY(-4px)', transition: '0.3s', boxShadow: 4 }
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h5" sx={{ color: '#5C005D', mb: 1 }}>
                        {country.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {(() => {
                          if (country.useLegacyCities) return '90+ cities covered';
                          const total = country.cities.length + (country.majorCities || []).length;
                          if (total === 0) return 'Nationwide coverage';
                          return `${total} ${total === 1 ? 'city' : 'cities'} covered`;
                        })()}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', color: '#5C005D' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>View locations</Typography>
                        <ArrowIcon sx={{ fontSize: 16, ml: 0.5 }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* About section */}
        <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: 'white' }}>
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h2" sx={{ color: '#5C005D', mb: 3, fontSize: { xs: '1.75rem', md: '3rem' } }}>
                  Dhol Entertainment Across {continent.name}
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: '1.1rem' }}>
                  Imperial Drummers provides professional Punjabi dhol entertainment for weddings and events
                  throughout {continent.name}. Whether you're planning a destination wedding or a cultural
                  celebration abroad, our experienced dhol players travel internationally to deliver
                  unforgettable performances.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: '1.1rem' }}>
                  With over 2000 events performed worldwide, we bring the same energy, professionalism, and
                  authenticity to every performance, no matter the location. Our team handles all travel
                  logistics so you can focus on enjoying your special day.
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 4, backgroundColor: '#f8f5fb', borderRadius: 2 }}>
                  <Typography variant="h5" sx={{ color: '#5C005D', mb: 3 }}>
                    Why Book International Dhol?
                  </Typography>
                  {[
                    "UK-trained professional dhol players",
                    "International travel experience",
                    "Full logistics coordination",
                    "Authentic Punjabi & Bollywood performances",
                    "Flexible packages for destination events",
                    "Multilingual team for easy communication"
                  ].map((item, i) => (
                    <Typography key={i} variant="body1" sx={{ mb: 1.5, display: 'flex', alignItems: 'center' }}>
                      <Box component="span" sx={{ color: '#ffd700', mr: 1, fontWeight: 'bold' }}>&#10003;</Box>
                      {item}
                    </Typography>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Booking Form */}
        <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: '#f8f5fb' }}>
          <BookingForm />
        </Box>

        <Footer />
      </Box>
    </>
  );
};

export default ContinentPage;
